import { FormEvent, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useApplyAuthzCommand,
  useAuthzAdminStatus,
  useAuthzAudit,
  useAuthzModels,
  usePreviewAuthzCommand,
} from '@/api/hooks';
import type {
  AuthzAdminCommandKind,
  AuthzAdminStatus,
  AuthzOperationalState,
  AuthzRelationshipCommandPayload,
} from '@/api/types';
import { ErrorCard } from '@/components/ErrorCard';
import { JsonView } from '@/components/JsonView';
import { MaterialIcon } from '@/components/MaterialIcon';
import { SkeletonRow } from '@/components/SkeletonRow';
import { StatusBadge, type StatusBadgeVariant } from '@/components/StatusBadge';
import { useToast } from '@/components/Toast';
import { PageHeader, Panel, PrimaryButton, Select, Td, TextInput, Th } from './pageParts';

type View = 'status' | 'models' | 'commands' | 'audit';

const VIEWS: readonly { id: View; label: string; icon: string }[] = [
  { id: 'status', label: 'Status', icon: 'monitor_heart' },
  { id: 'models', label: 'Models', icon: 'schema' },
  { id: 'commands', label: 'Commands', icon: 'terminal' },
  { id: 'audit', label: 'Audit', icon: 'history' },
];

const EMPTY_COMMAND = {
  application_id: '',
  domain: '',
  resource_type: 'knowledge_base',
  resource_id: '',
  relation: 'viewer',
  subject_type: 'user',
  subject_id: '',
  subject_relation: '',
  source_kind: 'admin',
  source_id: '',
  source_revision: '',
  expected_source_sequence: 0,
  next_source_sequence: 1,
  operation: 'touch' as 'touch' | 'delete',
  permission: 'search',
  idempotency_key: '',
  reason: '',
};

function stateVariant(state: AuthzOperationalState | string): StatusBadgeVariant {
  if (state === 'ready' || state === 'published' || state === 'staged') return 'success';
  if (state === 'degraded') return 'danger';
  if (state === 'disabled' || state === 'not_connected') return 'warning';
  return 'neutral';
}

function StateBadge({ state }: { state: AuthzOperationalState | string }) {
  return <StatusBadge variant={stateVariant(state)} dot>{state}</StatusBadge>;
}

function StatusView({ status }: { status: AuthzAdminStatus }) {
  const components = [
    ['Runtime', status.runtime.state, status.runtime.enabled ? 'enabled' : 'disabled'],
    ['Engine', status.engine.state, status.engine.detail],
    ['Models', status.models.state, `${status.models.active.length} active`],
    ['Watch', status.watch.state, status.watch.detail],
    ['Reconcile', status.reconcile.state, status.reconcile.detail],
    ['Exact Decision', status.last_exact_decision.state, status.last_exact_decision.detail],
  ] as const;
  return (
    <div className="flex flex-col gap-panel-gap">
      <div className="grid grid-cols-1 gap-panel-gap md:grid-cols-2 xl:grid-cols-3">
        {components.map(([label, state, detail]) => (
          <Panel key={label} title={label}>
            <div className="flex items-center justify-between gap-stack-md">
              <StateBadge state={state} />
              <span className="text-right font-code-md text-code-md text-on-surface-variant">{detail}</span>
            </div>
          </Panel>
        ))}
      </div>
      <Panel title="Control Queues">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead><tr className="border-b border-outline-variant bg-[#EBECF0] font-label-caps text-label-caps uppercase"><Th>component</Th><Th>state</Th><Th>pending / active</Th><Th>failed / stale</Th></tr></thead>
            <tbody className="divide-y divide-outline-variant font-body-sm text-body-sm">
              <tr><Td>Revocation fences</Td><Td><StateBadge state={status.fences.state} /></Td><Td>{status.fences.active ?? '-'}</Td><Td>-</Td></tr>
              <tr><Td>Subject sources</Td><Td><StateBadge state={status.sources.state} /></Td><Td>{status.sources.pending ?? '-'}</Td><Td>{(status.sources.stale_or_expired ?? 0) + (status.sources.failed_or_missing ?? 0)}</Td></tr>
              <tr><Td>Mutation intents</Td><Td><StateBadge state={status.mutation_intents.state} /></Td><Td>{status.mutation_intents.pending ?? '-'}</Td><Td>{status.mutation_intents.failed ?? '-'}</Td></tr>
              <tr><Td>Decision spool</Td><Td><StateBadge state={status.decision_spool.state} /></Td><Td>{status.decision_spool.pending ?? '-'}</Td><Td>{status.decision_spool.failed ?? '-'}</Td></tr>
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function ModelsView() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const models = useAuthzModels();
  const preview = usePreviewAuthzCommand();
  const apply = useApplyAuthzCommand();
  const [modelId, setModelId] = useState<string>();
  const [reason, setReason] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [previewResult, setPreviewResult] = useState<ReturnType<typeof usePreviewAuthzCommand>['data']>();

  function previewModel(nextModelId: string) {
    setModelId(nextModelId);
    setReason('');
    setIdempotencyKey(`model-publish-${nextModelId}-${Date.now()}`);
    setPreviewResult(undefined);
    preview.mutate({ kind: 'model_publish', payload: { model_id: nextModelId } }, {
      onSuccess: setPreviewResult,
    });
  }

  function publishModel() {
    if (!modelId || !previewResult?.apply_supported || !reason || !idempotencyKey) return;
    apply.mutate({
      kind: 'model_publish',
      payload: { model_id: modelId },
      preview_hash: previewResult.preview_hash,
      base_revision: previewResult.base_revision,
      idempotency_key: idempotencyKey,
      reason,
    }, {
      onSuccess: () => {
        toast.push({ message: 'Model published', variant: 'success' });
        setModelId(undefined);
        setPreviewResult(undefined);
        void queryClient.invalidateQueries({ queryKey: ['admin-authz-models'] });
        void queryClient.invalidateQueries({ queryKey: ['admin-authz-status'] });
        void queryClient.invalidateQueries({ queryKey: ['admin-authz-audit'] });
      },
    });
  }

  const error = preview.error ?? apply.error;
  return (
    <div className="flex flex-col gap-panel-gap">
      {error ? <ErrorCard title="Model publish failed" status={error.status} body={error.body} /> : null}
      {models.isPending ? <Panel title="Model Artifacts"><SkeletonRow count={4} cells={7} /></Panel> : models.error ? <ErrorCard title="Models request failed" status={models.error.status} body={models.error.body} /> : (
        <Panel title="Model Artifacts">
          <div className="overflow-x-auto"><table className="w-full border-collapse text-left"><thead><tr className="border-b border-outline-variant bg-[#EBECF0] font-label-caps text-label-caps uppercase"><Th>application / domain</Th><Th>version</Th><Th>status</Th><Th>schema hash</Th><Th>engine revision</Th><Th>action</Th></tr></thead><tbody className="divide-y divide-outline-variant font-body-sm text-body-sm">
            {(models.data ?? []).map((model) => <tr key={model.id}><Td>{model.application_id} / {model.domain}</Td><Td>{model.version}</Td><Td><StateBadge state={model.status} /></Td><Td className="font-code-md text-code-md">{model.schema_hash.slice(0, 16)}</Td><Td className="font-code-md text-code-md">{model.engine_revision_hash?.slice(0, 16) ?? '-'}</Td><Td>{model.status === 'staged' ? <PrimaryButton type="button" disabled={preview.isPending || apply.isPending} onClick={() => previewModel(model.id)}><MaterialIcon name="preview" size={15} />Preview</PrimaryButton> : '-'}</Td></tr>)}
            {(models.data ?? []).length === 0 ? <tr><Td className="text-on-surface-variant">No model artifacts</Td><Td>-</Td><Td>-</Td><Td>-</Td><Td>-</Td><Td>-</Td></tr> : null}
          </tbody></table></div>
        </Panel>
      )}
      {previewResult ? <Panel title="Bound Publish Preview">
        <div className="mb-stack-md flex flex-wrap items-center gap-stack-md"><StateBadge state={previewResult.status} /><span className="font-code-md text-code-md">base_revision={previewResult.base_revision}</span><span className="font-code-md text-code-md">hash={previewResult.preview_hash.slice(0, 16)}</span></div>
        <JsonView value={previewResult.model ?? previewResult} />
        <div className="mt-stack-md grid grid-cols-1 gap-stack-md md:grid-cols-2">
          <TextInput aria-label="model publish reason" required placeholder="reason" value={reason} onChange={(event) => setReason(event.target.value)} />
          <TextInput aria-label="model publish idempotency key" required placeholder="idempotency_key" value={idempotencyKey} onChange={(event) => setIdempotencyKey(event.target.value)} />
        </div>
        <PrimaryButton type="button" className="mt-stack-md" disabled={!previewResult.apply_supported || apply.isPending || !reason || !idempotencyKey} onClick={publishModel}><MaterialIcon name="publish" size={15} />Publish</PrimaryButton>
      </Panel> : null}
    </div>
  );
}

function CommandsView() {
  const [kind, setKind] = useState<AuthzAdminCommandKind>('relationship');
  const [form, setForm] = useState(EMPTY_COMMAND);
  const [previewResult, setPreviewResult] = useState<ReturnType<typeof usePreviewAuthzCommand>['data']>(undefined);
  const toast = useToast();
  const queryClient = useQueryClient();
  const preview = usePreviewAuthzCommand();
  const apply = useApplyAuthzCommand();

  function update<K extends keyof typeof EMPTY_COMMAND>(key: K, value: (typeof EMPTY_COMMAND)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setPreviewResult(undefined);
  }

  function changeKind(value: AuthzAdminCommandKind) {
    setKind(value);
    setPreviewResult(undefined);
  }

  function payload(): AuthzRelationshipCommandPayload | Record<string, unknown> {
    if (kind === 'reconcile') {
      return { application_id: form.application_id, domain: form.domain };
    }
    return {
      application_id: form.application_id,
      domain: form.domain,
      resource_type: form.resource_type,
      resource_id: form.resource_id,
      relation: form.relation,
      subject_type: form.subject_type,
      subject_id: form.subject_id,
      subject_relation: form.subject_relation || null,
      caveat_context: null,
      source_kind: form.source_kind,
      source_id: form.source_id,
      source_revision: form.source_revision,
      expected_source_sequence: form.expected_source_sequence,
      next_source_sequence: form.next_source_sequence,
      operation: kind === 'relationship' ? form.operation : null,
      permission: kind === 'revoke' ? form.permission : null,
    };
  }

  function previewCommand(event: FormEvent) {
    event.preventDefault();
    preview.mutate({ kind, payload: payload() }, { onSuccess: setPreviewResult });
  }

  function applyCommand() {
    if (!previewResult || !previewResult.apply_supported) return;
    apply.mutate({
      kind,
      payload: payload(),
      preview_hash: previewResult.preview_hash,
      base_revision: previewResult.base_revision,
      idempotency_key: form.idempotency_key,
      reason: form.reason,
    }, {
      onSuccess: () => {
        toast.push({ message: 'AuthZ command accepted', variant: 'success' });
        setPreviewResult(undefined);
        void queryClient.invalidateQueries({ queryKey: ['admin-authz-status'] });
        void queryClient.invalidateQueries({ queryKey: ['admin-authz-audit'] });
      },
    });
  }

  const error = preview.error ?? apply.error;
  return (
    <div className="flex flex-col gap-panel-gap">
      <div role="tablist" aria-label="AuthZ command kind" className="inline-flex w-fit rounded border border-outline-variant bg-surface p-unit">
        {(['relationship', 'revoke', 'reconcile'] as const).map((value) => <button key={value} type="button" role="tab" aria-selected={kind === value} onClick={() => changeKind(value)} className={`rounded px-stack-md py-stack-sm font-label-caps text-label-caps uppercase ${kind === value ? 'bg-primary-container text-on-primary' : 'text-on-surface-variant'}`}>{value}</button>)}
      </div>
      {kind === 'reconcile' ? <Panel title="Reconcile Command"><form onSubmit={previewCommand} className="grid grid-cols-1 gap-stack-md md:grid-cols-2"><TextInput aria-label="command application_id" required placeholder="application_id" value={form.application_id} onChange={(e) => update('application_id', e.target.value)} /><TextInput aria-label="command domain" required placeholder="domain" value={form.domain} onChange={(e) => update('domain', e.target.value)} /><TextInput aria-label="command idempotency_key" required placeholder="idempotency_key" value={form.idempotency_key} onChange={(e) => update('idempotency_key', e.target.value)} /><TextInput aria-label="command reason" required placeholder="reason" value={form.reason} onChange={(e) => update('reason', e.target.value)} /><div className="md:col-span-2"><PrimaryButton type="submit" disabled={preview.isPending}><MaterialIcon name="preview" size={15} />Preview</PrimaryButton></div></form></Panel> : (
        <Panel title={kind === 'relationship' ? 'Relationship Command' : 'Revoke Command'}>
          <form onSubmit={previewCommand} className="grid grid-cols-1 gap-stack-md md:grid-cols-2 xl:grid-cols-4">
            <TextInput aria-label="command application_id" required placeholder="application_id" value={form.application_id} onChange={(e) => update('application_id', e.target.value)} />
            <TextInput aria-label="command domain" required placeholder="domain" value={form.domain} onChange={(e) => update('domain', e.target.value)} />
            <TextInput aria-label="command resource_type" required placeholder="resource_type" value={form.resource_type} onChange={(e) => update('resource_type', e.target.value)} />
            <TextInput aria-label="command resource_id" required placeholder="resource_id" value={form.resource_id} onChange={(e) => update('resource_id', e.target.value)} />
            <TextInput aria-label="command relation" required placeholder="relation" value={form.relation} onChange={(e) => update('relation', e.target.value)} />
            <TextInput aria-label="command subject_type" required placeholder="subject_type" value={form.subject_type} onChange={(e) => update('subject_type', e.target.value)} />
            <TextInput aria-label="command subject_id" required placeholder="subject_id" value={form.subject_id} onChange={(e) => update('subject_id', e.target.value)} />
            <TextInput aria-label="command subject_relation" placeholder="subject_relation (optional)" value={form.subject_relation} onChange={(e) => update('subject_relation', e.target.value)} />
            <TextInput aria-label="command source_kind" required placeholder="source_kind" value={form.source_kind} onChange={(e) => update('source_kind', e.target.value)} />
            <TextInput aria-label="command source_id" required placeholder="source_id" value={form.source_id} onChange={(e) => update('source_id', e.target.value)} />
            <TextInput aria-label="command source_revision" required placeholder="source_revision" value={form.source_revision} onChange={(e) => update('source_revision', e.target.value)} />
            {kind === 'relationship' ? <Select aria-label="command operation" value={form.operation} onChange={(e) => update('operation', e.target.value as 'touch' | 'delete')}><option value="touch">touch</option><option value="delete">delete</option></Select> : <TextInput aria-label="command permission" required placeholder="permission" value={form.permission} onChange={(e) => update('permission', e.target.value)} />}
            <TextInput aria-label="command expected_source_sequence" type="number" min={0} value={form.expected_source_sequence} onChange={(e) => update('expected_source_sequence', Number(e.target.value))} />
            <TextInput aria-label="command next_source_sequence" type="number" min={1} value={form.next_source_sequence} onChange={(e) => update('next_source_sequence', Number(e.target.value))} />
            <TextInput aria-label="command idempotency_key" required placeholder="idempotency_key" value={form.idempotency_key} onChange={(e) => update('idempotency_key', e.target.value)} />
            <TextInput aria-label="command reason" required placeholder="reason" value={form.reason} onChange={(e) => update('reason', e.target.value)} />
            <div className="md:col-span-2 xl:col-span-4"><PrimaryButton type="submit" disabled={preview.isPending}><MaterialIcon name="preview" size={15} />Preview</PrimaryButton></div>
          </form>
        </Panel>
      )}
      {error ? <ErrorCard title="Command failed" status={error.status} body={error.body} /> : null}
      {previewResult ? <Panel title="Bound Preview"><div className="mb-stack-md flex flex-wrap items-center gap-stack-md"><StateBadge state={previewResult.status} /><span className="font-code-md text-code-md">base_revision={previewResult.base_revision}</span><span className="font-code-md text-code-md">hash={previewResult.preview_hash.slice(0, 16)}</span></div><JsonView value={previewResult} /><PrimaryButton type="button" className="mt-stack-md" disabled={!previewResult.apply_supported || apply.isPending || !form.idempotency_key || !form.reason} onClick={applyCommand}><MaterialIcon name="check_circle" size={15} />Apply</PrimaryButton></Panel> : null}
    </div>
  );
}

function AuditView() {
  const [limit, setLimit] = useState(50);
  const audit = useAuthzAudit(limit);
  return (
    <div className="flex flex-col gap-panel-gap">
      <div className="flex justify-end"><Select aria-label="AuthZ audit limit" value={limit} onChange={(e) => setLimit(Number(e.target.value))}><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option></Select></div>
      {audit.isPending ? <Panel><SkeletonRow count={6} cells={8} /></Panel> : audit.error ? <ErrorCard title="AuthZ audit request failed" status={audit.error.status} body={audit.error.body} /> : <Panel title={`Mutation Audit · ${audit.data?.length ?? 0}`}><div className="overflow-x-auto"><table className="w-full border-collapse text-left"><thead><tr className="border-b border-outline-variant bg-[#EBECF0] font-label-caps text-label-caps uppercase"><Th>occurred</Th><Th>actor</Th><Th>application / domain</Th><Th>operation</Th><Th>phase</Th><Th>outcome</Th><Th>revision</Th><Th>reason / model</Th></tr></thead><tbody className="divide-y divide-outline-variant font-body-sm text-body-sm">{(audit.data ?? []).map((event) => <tr key={event.id}><Td>{new Date(event.occurred_at).toLocaleString()}</Td><Td>{event.actor_kind}<div className="font-code-md text-code-md text-on-surface-variant">{event.actor_ref_hash.slice(0, 12)}</div></Td><Td>{event.application_id} / {event.domain}</Td><Td>{event.operation}</Td><Td>{event.event_phase}</Td><Td><StatusBadge variant={event.outcome === 'success' || event.outcome === 'accepted' ? 'success' : 'neutral'}>{event.outcome}</StatusBadge></Td><Td className="font-code-md text-code-md">{event.revision_hash?.slice(0, 12) ?? '-'}</Td><Td className="font-code-md text-code-md"><div>{event.reason_hash.slice(0, 12)}</div><div>{event.model_id?.slice(0, 12) ?? '-'}</div></Td></tr>)}</tbody></table></div></Panel>}
    </div>
  );
}

export function AuthzAdminPage() {
  const [view, setView] = useState<View>('status');
  const status = useAuthzAdminStatus();
  return (
    <div className="flex flex-col gap-panel-gap">
      <PageHeader title="AuthZ Control" subtitle="Runtime state, model lifecycle, relationship commands and mutation audit." meta={status.data ? <StateBadge state={status.data.overall} /> : null} />
      <div role="tablist" aria-label="AuthZ control views" className="flex w-full overflow-x-auto border-b border-outline-variant">
        {VIEWS.map((item) => <button key={item.id} type="button" role="tab" aria-selected={view === item.id} onClick={() => setView(item.id)} className={`inline-flex min-w-28 items-center justify-center gap-unit border-b-2 px-stack-md py-stack-sm font-label-caps text-label-caps uppercase ${view === item.id ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant'}`}><MaterialIcon name={item.icon} size={16} />{item.label}</button>)}
      </div>
      {view === 'status' ? status.isPending ? <Panel><SkeletonRow count={6} cells={4} /></Panel> : status.error ? <ErrorCard title="AuthZ status request failed" status={status.error.status} body={status.error.body} onRetry={() => void status.refetch()} /> : status.data ? <StatusView status={status.data} /> : null : null}
      {view === 'models' ? <ModelsView /> : null}
      {view === 'commands' ? <CommandsView /> : null}
      {view === 'audit' ? <AuditView /> : null}
    </div>
  );
}
